import React, { FC, useEffect, useState } from 'react';
import { Button, Col, Row, Skeleton } from 'antd';
import { InfoTooltip } from 'common/components/InfoTooltip';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getMonitorSalesProductSalesModel } from 'app/store/reducers/monitorSales.reducer';
import { IMonitorSalesParams, IMonitorSalesProductItem } from 'entities/MonitorSales/MonitorSales.models';
import { DonutChart } from 'entities/MonitorSales/components/DonutChart';
import { ProductSalesModal } from 'entities/MonitorSales/components/ProductSalesModal';
import { CategoryColorLegend } from 'entities/MonitorSales/components/CategoryColorLegend';
import { sortProductSalesBySalesPercentDesc } from 'entities/MonitorSales/monitorSales.helpers';
import { buildCategoryRanks, pickCategoryShade } from 'entities/MonitorSales/monitorSales.categoryColors';

interface IComponentProps {
  params?: IMonitorSalesParams;
}

export const ProductSalesBlock: FC<IComponentProps> = ({ params }) => {
  const dispatch = useAppDispatch();
  const { monitorSalesProductSalesModel, monitorSalesProductSalesModelLoading } = useAppSelector(
    (state) => state.monitorSales
  );
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [openProductSalesModal, setOpenProductSalesModal] = useState<boolean>(false);

  useEffect(() => {
    dispatch(getMonitorSalesProductSalesModel(params));
  }, [params?.storeCode, params?.week]);

  const products: IMonitorSalesProductItem[] = sortProductSalesBySalesPercentDesc(
    monitorSalesProductSalesModel?.products ?? []
  );
  const topProducts = products.slice(0, 10);
  const { rankInCategory, countByCategory } = buildCategoryRanks(products);
  const productColors = topProducts.map(
    (item) =>
      pickCategoryShade(item.category, rankInCategory.get(item) ?? 0, countByCategory.get(item.category) ?? 1)
        .background
  );
  const chartItems = topProducts.map((item, i) => ({
    salesPercent: item.salesPercent,
    color: productColors[i],
  }));

  const handleOpenProductSalesModal = (): void => {
    setOpenProductSalesModal(true);
  };

  const closeProductSalesModal = (): void => {
    setOpenProductSalesModal(false);
  };

  return (
    <div className="ms-block">
      <div className="ms__header">
        <div className="flex flex-align-center">
          <span className="mr-5 ff-medium fs-xs">Product Sales (Top-10)</span>
          <Button className="btn-secondary h-24" onClick={handleOpenProductSalesModal}>
            Show All
          </Button>
        </div>
        <InfoTooltip
          title={
            <div>
              <div className="mb-3 ff-medium fs-xxs">Product Sales</div>
              <div className="mb-4 ff-medium fs-xxxs color-gray-1">See which products sell the most!</div>
              <CategoryColorLegend />
              <div className="mb-4 ff-medium fs-xxxs color-gray-1">Tap “Show All” to see all products.</div>
            </div>
          }
        />
      </div>

      <div className="ms__body">
        {monitorSalesProductSalesModelLoading ? (
          <Skeleton active paragraph={{ rows: 8 }} title={false} />
        ) : (
          <Row gutter={8} align="middle">
            <Col span={16} className="ms-products__chart">
              <DonutChart items={chartItems} hoveredIndex={hoveredIndex} onHoverChange={setHoveredIndex} />
            </Col>
            <Col span={8} className="ms-products__list">
              {topProducts.map((item, i) => (
                <Row
                  key={item.name}
                  gutter={8}
                  className={`mb-4 ms-products__item ${
                    hoveredIndex !== null && hoveredIndex !== i ? 'ms-products__item--dimmed' : ''
                  }`}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <Col>
                    <Row gutter={6} align="middle">
                      <Col className="ms-products__dot" style={{ backgroundColor: productColors[i] }} />
                      <Col className="ms-products__name">{item.name}</Col>
                    </Row>
                  </Col>
                  <Col>
                    <Row gutter={4}>
                      <Col className="color-gray-1">₱{item.sales.toLocaleString('en-US')}</Col>
                      <Col className="color-gray-1">·</Col>
                      <Col className="ms-products__pct">{item.salesPercent}%</Col>
                    </Row>
                  </Col>
                </Row>
              ))}
            </Col>
          </Row>
        )}
      </div>

      <ProductSalesModal open={openProductSalesModal} products={products} onCancel={closeProductSalesModal} />
    </div>
  );
};
